import { useState } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'
import {
  Button,
  Card,
  ChannelAvatar,
  Dialog,
  Flex,
  Link,
  Notice,
  RefreshCwIcon,
  Text,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import type { Service } from '../../types'
import { resetChannelConnectionsState } from '../../../store/channelConnectionsReducer'

import styles from './MultipleChannelsWarningModal.module.css'
import capitalize from 'lodash/capitalize'
import { getServiceName } from '../../utils'
import type { Channel } from '../../../../../common/types'

const HC_UTM_PARAMS =
  'utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more'

const LinkedInPermissionsMessage = (
  <>
    <Text as="p">
      Make sure you&apos;re signing in with the LinkedIn account that has{' '}
      <Link
        external
        href={`https://support.buffer.com/articles/using-linkedin-with-buffer-K7tRkGD3mH?${HC_UTM_PARAMS}`}
        color="inherit"
      >
        Super Admin permissions for the Pages
      </Link>{' '}
      you&apos;re looking to connect.
    </Text>
  </>
)

const MultipleChannelsWarningModal = ({
  channel,
  onContinue,
}: {
  channel: Channel
  onContinue: () => void
}): JSX.Element => {
  const [open, setOpen] = useState(true)
  const dispatch = useDispatch()

  const handleClose = (): void => {
    setOpen(false)
    dispatch(resetChannelConnectionsState({}))
  }

  const handleContinue = (): void => {
    setOpen(false)
    onContinue()
  }

  const isLinkedInPage =
    channel.service === 'linkedin' && channel.type === 'page'

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <Dialog.Content size="xlarge">
        <Dialog.CloseButton onClick={handleClose} />
        <Dialog.Header className={styles.header}>
          <div className={styles.iconWrapper}>
            <RefreshCwIcon className={styles.icon} size="large" />
          </div>
          <Dialog.Title>
            Quick check: Are you signed in to the right{' '}
            {getServiceName(channel.service as Service)} account?
          </Dialog.Title>
          <Dialog.Description>
            <Text color="subtle">
              We can only refresh the account you&apos;re currently signed in to
              on {getServiceName(channel.service as Service)}
            </Text>
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          <Flex direction="column" gap="md" align="center">
            {isLinkedInPage && (
              <Notice variant="info" className={styles.linkedinNotice}>
                {LinkedInPermissionsMessage}
              </Notice>
            )}
            <Card className={styles.card}>
              <ChannelAvatar
                alt={channel.name}
                channel={channel.service as ChannelType}
                src={channel.avatar}
              />
              <div className={styles.channelInfo}>
                <Text weight="bold">{channel.name}</Text>
                <Text color="subtle">{capitalize(channel.type)}</Text>
              </div>
              <Button onClick={handleContinue}>Yes, Refresh Now</Button>
            </Card>
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Refresh Later
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default MultipleChannelsWarningModal
